import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Drawer, Spin, message, Icon, Tag, Input, Button, Select, Tooltip, Modal, DatePicker, Checkbox } from "antd";
import MonthPicker from "~/_shared/components/MonthPicker";
import styled from "styled-components";
import moment from "moment";

import InputCurrency, { toBRL, currency2Float } from "~/components/UI/InputCurrency";
import * as bankReconciliationActions from "../actions";

// Header Card with gradient
var HeaderCard = styled.div`
    background: linear-gradient(135deg, ${function(props) {
        return props.type === 'credit' ? '#f6ffed 0%, #e6f7ff 100%' : '#fff1f0 0%, #fff7e6 100%';
    }});
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
`;

var HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

var HeaderTitleText = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
`;

var HeaderSubtitle = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 2px;
`;

var StatsRow = styled.div`
    display: flex;
    gap: 16px;
`;

var StatItem = styled.div`
    flex: 1;
    text-align: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 6px;
`;

var StatValue = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    line-height: 1.2;
`;

var StatLabel = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 4px;
`;

// Mode Tabs
var TabsContainer = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
`;

var TabButton = styled.button`
    flex: 1;
    padding: 12px 16px;
    border: 1px solid ${function(props) { return props.active ? '#1890ff' : '#d9d9d9'; }};
    border-radius: 6px;
    background: ${function(props) { return props.active ? '#e6f7ff' : '#fff'; }};
    color: ${function(props) { return props.active ? '#1890ff' : 'rgba(0, 0, 0, 0.65)'; }};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover {
        border-color: #1890ff;
        color: #1890ff;
    }
`;

// List
var ListContainer = styled.div`
    max-height: calc(100vh - 340px);
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #bfbfbf;
    }
`;

var ItemCard = styled.div`
    background: #fff;
    border: 1px solid ${function(props) {
        if (props.selected) return '#1890ff';
        if (props.exact) return props.isDebit ? '#f5222d' : '#52c41a';
        return '#e8e8e8';
    }};
    border-radius: 8px;
    padding: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    ${function(props) { return props.selected ? 'box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);' : ''; }}
    ${function(props) {
        if (props.exact && !props.selected) {
            var color = props.isDebit ? '#f5222d' : '#52c41a';
            return 'border-left: 3px solid ' + color + ';';
        }
        return '';
    }}

    &:hover {
        border-color: ${function(props) { return props.selected ? '#1890ff' : '#40a9ff'; }};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

var ItemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #e8e8e8;
`;

var ItemDate = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    gap: 4px;
`;

var ItemContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

var ItemInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

var CheckCircle = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid ${function(props) { return props.checked ? '#1890ff' : '#d9d9d9'; }};
    background: ${function(props) { return props.checked ? '#1890ff' : '#fff'; }};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
`;

var ItemName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

var ItemRight = styled.div`
    text-align: right;
    flex-shrink: 0;
`;

var ItemAmount = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${function(props) {
        if (props.exact) return props.isDebit ? '#f5222d' : '#52c41a';
        return 'rgba(0, 0, 0, 0.85)';
    }};
`;

var ExactBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
    background: ${function(props) { return props.isDebit ? '#fff1f0' : '#f6ffed'; }};
    color: ${function(props) { return props.isDebit ? '#f5222d' : '#52c41a'; }};
    margin-top: 4px;
`;

// Selection Summary
var SelectionCard = styled.div`
    background: ${function(props) {
        if (props.match) return props.isDebit ? '#fff1f0' : '#f6ffed';
        return '#fffbe6';
    }};
    border: 1px solid ${function(props) {
        if (props.match) return props.isDebit ? '#ffa39e' : '#b7eb8f';
        return '#ffe58f';
    }};
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

var SelectionText = styled.span`
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    gap: 6px;
`;

var SelectionValue = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${function(props) {
        if (props.match) return props.isDebit ? '#f5222d' : '#52c41a';
        return '#faad14';
    }};
`;

// Form styles
var FormSection = styled.div`
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 20px;
`;

var FormGroup = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`;

var FormLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 8px;
`;

var FormInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    &::placeholder {
        color: rgba(0, 0, 0, 0.25);
    }
`;

var FormSelect = styled.select`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    font-size: 14px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
`;

var InfoBox = styled.div`
    background: ${function(props) { return props.type === 'debit' ? '#fff1f0' : '#f6ffed'; }};
    border: 1px solid ${function(props) { return props.type === 'debit' ? '#ffa39e' : '#b7eb8f'; }};
    border-radius: 6px;
    padding: 12px 16px;
    margin-top: 16px;
`;

var InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;

    &:not(:last-child) {
        border-bottom: 1px dashed ${function(props) { return props.type === 'debit' ? '#ffa39e' : '#b7eb8f'; }};
    }
`;

var InfoLabel = styled.span`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
`;

var InfoValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
`;

// Empty State
var EmptyState = styled.div`
    text-align: center;
    padding: 60px 40px;
    color: rgba(0, 0, 0, 0.45);
`;

var EmptyIcon = styled.div`
    font-size: 64px;
    margin-bottom: 16px;
    color: #d9d9d9;
`;

var EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 8px;
`;

var EmptyDescription = styled.div`
    font-size: 14px;
`;

// Search & Sort Bar
var SearchSortBar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    align-items: center;
`;

var SortButton = styled.button`
    padding: 7px 12px;
    border: 1px solid ${function(props) { return props.active ? '#1890ff' : '#d9d9d9'; }};
    border-radius: 4px;
    background: ${function(props) { return props.active ? '#e6f7ff' : '#fff'; }};
    color: ${function(props) { return props.active ? '#1890ff' : 'rgba(0,0,0,0.65)'; }};
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        border-color: #1890ff;
        color: #1890ff;
    }
`;

// Footer
var DrawerFooter = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

var FooterInfo = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
`;

var FooterActions = styled.div`
    display: flex;
    gap: 8px;
`;

var ActionButton = styled.button`
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 6px;

    ${function(props) {
        if (props.primary) {
            var color = props.danger ? '#f5222d' : '#52c41a';
            var hoverColor = props.danger ? '#ff4d4f' : '#73d13d';
            return '\n                background: ' + color + ';\n                border: 1px solid ' + color + ';\n                color: #fff;\n\n                &:hover:not(:disabled) { background: ' + hoverColor + '; border-color: ' + hoverColor + '; }\n                &:disabled { background: #d9d9d9; border-color: #d9d9d9; cursor: not-allowed; }\n            ';
        }
        return '\n            background: #fff;\n            border: 1px solid #d9d9d9;\n            color: rgba(0, 0, 0, 0.65);\n\n            &:hover { border-color: #40a9ff; color: #40a9ff; }\n        ';
    }}
`;

var DrawerReconcile = function() {
    var dispatch = useDispatch();

    var drawer = useSelector(function(state) {
        return state.bankReconciliation.drawer;
    });

    var categories = useSelector(function(state) {
        return state.settings['financeiro.categoria_contas_pagar'] || [];
    });

    var categoriesReceber = useSelector(function(state) {
        return state.settings['financeiro.categoria_contas_receber'] || [];
    });

    var customers = useSelector(function(state) {
        return (state.customers && state.customers.list) ? state.customers.list.filter(function(c) { return c.is_customer; }) : [];
    });

    var visible = drawer.visible;
    var data = drawer.data;
    var suggestions = drawer.suggestions;
    var isLoadingSuggestions = drawer.isLoadingSuggestions;
    var suggestionsLoaded = drawer.suggestionsLoaded;
    var recordTypes = drawer.recordTypes || ['mensalidades', 'extras', 'comissoes'];

    var [isSubmitting, setIsSubmitting] = useState(false);
    var [selectedItems, setSelectedItems] = useState([]);
    var [mode, setMode] = useState('select');
    var [customEntries, setCustomEntries] = useState([{ description: '', categoryId: '', amount: 0, dueAt: null }]);
    var [searchText, setSearchText] = useState('');
    var [sortBy, setSortBy] = useState('match');
    var [sortOrder, setSortOrder] = useState('asc');
    var [customerFilter, setCustomerFilter] = useState(null);
    var [periodMode, setPeriodMode] = useState('month'); // 'month', 'custom' ou 'all'
    var [periodMonth, setPeriodMonth] = useState(null);
    var [periodRange, setPeriodRange] = useState([null, null]);
    var [note, setNote] = useState('');
    var [noteModalOpen, setNoteModalOpen] = useState(false);
    var [noteDraft, setNoteDraft] = useState('');

    var getTypes = function() {
        if (!data) return [];
        return data.transaction_type === 'debit'
            ? ['comissoes', 'despesas']
            : ['mensalidades', 'extras'];
    };

    var getPeriodDates = function(pMode, pMonth, pRange) {
        if (pMode === 'month' && pMonth) {
            return {
                from: pMonth.clone().startOf('month').format('YYYY-MM-DD'),
                to: pMonth.clone().endOf('month').format('YYYY-MM-DD'),
            };
        }
        if (pMode === 'custom' && pRange[0] && pRange[1]) {
            return {
                from: pRange[0].format('YYYY-MM-DD'),
                to: pRange[1].format('YYYY-MM-DD'),
            };
        }
        return null;
    };

    var loadSuggestions = useCallback(function(pMode, pMonth, pRange) {
        if (!data || !data.id) return;
        var types = getTypes();
        if (pMode === 'all') {
            dispatch(bankReconciliationActions.getSuggestions(data.id, types, null, null, true));
            return;
        }
        var dates = getPeriodDates(pMode, pMonth, pRange);
        if (dates) {
            dispatch(bankReconciliationActions.getSuggestions(data.id, types, dates.from, dates.to));
        } else {
            dispatch(bankReconciliationActions.getSuggestions(data.id, types));
        }
        // Keep selected items when period/filters change — user may want to combine matches across months.
    }, [data, dispatch]);

    useEffect(function() {
        if (visible && data && data.id) {
            var initialMonth = moment(data.transaction_date);
            setPeriodMode('month');
            setPeriodMonth(initialMonth);
            setPeriodRange([null, null]);
            var types = getTypes();
            dispatch(bankReconciliationActions.getSuggestions(
                data.id, types,
                initialMonth.clone().startOf('month').format('YYYY-MM-DD'),
                initialMonth.clone().endOf('month').format('YYYY-MM-DD')
            ));
            setSelectedItems([]);
            setMode('select');
            setCustomEntries([{ description: data.description || '', categoryId: '', amount: parseFloat(data.amount) || 0, dueAt: moment(data.transaction_date) }]);
            setSearchText('');
            lastSearchRef.current = '';
            setSortBy('match');
            setSortOrder('asc');
            setNote('');
            // Pre-select customer if detected from CPF/CNPJ
            setCustomerFilter(data.detected_customer ? data.detected_customer.id : null);
        }
    }, [visible, data && data.id, data && data.transaction_type]);

    // A busca digitada vai para o servidor (com pausa curta entre teclas): o
    // que veio na tela é só o recorte mais perto do valor, e o título que a
    // pessoa procura pode estar em qualquer competência. Limpou o campo,
    // volta ao período escolhido.
    var lastSearchRef = useRef('');
    useEffect(function() {
        if (!visible || !data || !data.id) return;
        var term = (searchText || '').trim();
        if (term === lastSearchRef.current) return;

        var timer = setTimeout(function() {
            lastSearchRef.current = term;
            if (term.length >= 2) {
                dispatch(bankReconciliationActions.getSuggestions(data.id, getTypes(), null, null, false, term));
            } else if (term === '') {
                loadSuggestions(periodMode, periodMonth, periodRange);
            }
        }, 400);

        return function() { clearTimeout(timer); };
    }, [searchText, visible, data && data.id]);

    var handleClose = useCallback(function() {
        dispatch(bankReconciliationActions.setDrawer({
            visible: false,
            data: null,
            suggestions: [],
            suggestionsLoaded: false,
        }));
        setSelectedItems([]);
        setMode('select');
        setCustomEntries([{ description: '', categoryId: '', amount: 0 }]);
        setSearchText('');
        setSortBy('match');
        setSortOrder('asc');
        setCustomerFilter(null);
        setPeriodMode('month');
        setPeriodMonth(null);
        setPeriodRange([null, null]);
        setNote('');
        setNoteModalOpen(false);
        setNoteDraft('');
    }, [dispatch]);

    var handleSelect = useCallback(function(item) {
        setSelectedItems(function(prev) {
            var idx = -1;
            for (var i = 0; i < prev.length; i++) {
                if (prev[i].id === item.id && prev[i].type === item.type) { idx = i; break; }
            }
            if (idx >= 0) {
                var next = prev.slice();
                next.splice(idx, 1);
                return next;
            }
            return prev.concat([item]);
        });
    }, []);

    var selectedTotal = useMemo(function() {
        return selectedItems.reduce(function(sum, it) { return sum + (parseFloat(it.amount) || 0); }, 0);
    }, [selectedItems]);

    var handleSubmit = useCallback(function() {
        if (!data || !data.id) return;

        setIsSubmitting(true);

        if (mode === 'create') {
            // Reject partial rows so nothing gets silently dropped
            var incompleteIdx = -1;
            for (var i = 0; i < customEntries.length; i++) {
                var e = customEntries[i];
                var hasDesc = !!(e.description || '').trim();
                var hasAmt = (parseFloat(e.amount) || 0) > 0;
                if (hasDesc !== hasAmt) { incompleteIdx = i; break; }
            }
            if (incompleteIdx >= 0) {
                message.error('Lançamento #' + (incompleteIdx + 1) + ' está incompleto (descrição e valor são obrigatórios)');
                setIsSubmitting(false);
                return;
            }
            var validEntries = customEntries.filter(function(e) { return (e.description || '').trim() && (parseFloat(e.amount) || 0) > 0; });
            if (!validEntries.length) {
                message.error('Informe ao menos um lançamento com descrição e valor');
                setIsSubmitting(false);
                return;
            }
            console.log('[createAndReconcile] sending', validEntries.length, 'entries:', validEntries);

            dispatch(bankReconciliationActions.createAndReconcile({
                bank_statement_id: data.id,
                due_at: data.transaction_date,
                paid_at: data.transaction_date,
                entries: validEntries.map(function(e) {
                    return {
                        description: e.description.trim(),
                        amount: parseFloat(e.amount) || 0,
                        category_id: e.categoryId || null,
                        due_at: e.dueAt ? e.dueAt.format('YYYY-MM-DD') : null,
                    };
                }),
            }))
                .then(function() {
                    message.success('Conciliado com sucesso!');
                    setIsSubmitting(false);
                })
                .catch(function() {
                    message.error('Erro ao conciliar');
                    setIsSubmitting(false);
                });
        } else {
            if (!selectedItems.length) {
                message.error('Selecione pelo menos um item');
                setIsSubmitting(false);
                return;
            }

            // Store selected matches as { items: [...], note } — array shape supports multi-select
            var pending = { items: selectedItems.slice(), note: note || null };
            dispatch(bankReconciliationActions.setPendingMatch(data.id, pending));
            handleClose();
        }
    }, [data, mode, customEntries, selectedItems, note, dispatch]);

    var customEntriesTotal = useMemo(function() {
        return customEntries.reduce(function(s, e) { return s + (parseFloat(e.amount) || 0); }, 0);
    }, [customEntries]);

    var getTypeInfo = useCallback(function(type) {
        if (type === 'mensalidade') return { label: 'Mensalidade', color: 'blue' };
        if (type === 'extra') return { label: 'Extra', color: 'purple' };
        if (type === 'comissao') return { label: 'Comissão', color: 'orange' };
        if (type === 'despesa') return { label: 'Despesa', color: 'red' };
        return { label: 'Outro', color: 'default' };
    }, []);

    var getItemName = function(item) {
        return item.type === 'despesa'
            ? (item.description || '')
            : (item.customer ? item.customer.name : '');
    };

    // Codigo que liga o recebimento a conta a receber: ME#### na mensalidade e
    // #SOC### na cobranca vinda do societario. Despesa e extra avulso nao tem.
    var getItemCod = function(item) {
        return item && item.cod ? item.cod : '';
    };

    var handleSortClick = function(field) {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    var getSortArrow = function(field) {
        if (sortBy !== field) return '↕';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    var sortedSuggestions = useMemo(function() {
        if (!data) return suggestions;
        var search = searchText ? searchText.toLowerCase() : '';

        var filtered = suggestions.filter(function(item) {
            // Type filters
            if (item.type === 'comissao' && data.transaction_type === 'credit') return false;
            if (item.type === 'mensalidade' && data.transaction_type === 'debit') return false;
            if (item.type === 'extra' && data.transaction_type === 'debit') return false;
            // Customer filter
            if (customerFilter && item.customer && item.customer.id !== customerFilter) return false;
            // Text search
            if (search) {
                var customerCod = item.customer && item.customer.cod ? String(item.customer.cod) : '';
                var haystack = (getItemName(item) + ' ' + getItemCod(item) + ' ' + customerCod).toLowerCase();
                if (haystack.indexOf(search) === -1) return false;
            }
            return true;
        });

        return filtered.slice().sort(function(a, b) {
            if (sortBy === 'name') {
                var nameA = getItemName(a);
                var nameB = getItemName(b);
                var nameCmp = nameA.localeCompare(nameB, 'pt-BR');
                return sortOrder === 'asc' ? nameCmp : -nameCmp;
            }
            if (sortBy === 'amount') {
                var amtCmp = a.amount - b.amount;
                return sortOrder === 'asc' ? amtCmp : -amtCmp;
            }
            // Default (match): exact match first
            var aExact = Math.abs(a.amount - data.amount) < 0.01;
            var bExact = Math.abs(b.amount - data.amount) < 0.01;
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            return 0;
        });
    }, [suggestions, data, searchText, sortBy, sortOrder, customerFilter]);

    var isExactMatch = selectedItems.length > 0 && data && Math.abs(selectedTotal - data.amount) < 0.01;
    var hasValidCustom = customEntries.some(function(e) { return (e.description || '').trim() && (parseFloat(e.amount) || 0) > 0; });
    var isDisabled = (mode === 'select' && !selectedItems.length) || (mode === 'create' && !hasValidCustom);

    if (!data) return null;

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon type="link" style={{ color: '#1890ff', fontSize: 18 }} />
                    <span style={{ fontWeight: 600 }}>Buscar Lançamento</span>
                </div>
            }
            placement="right"
            closable={true}
            onClose={handleClose}
            visible={visible}
            width={'80vw'}
            bodyStyle={{ paddingBottom: 80, background: '#f5f5f5' }}
        >
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* Left: Bank statement card (sticky) */}
            <div style={{ width: 320, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
                <div style={{
                    padding: '20px',
                    borderRadius: 12,
                    background: data.transaction_type === 'credit'
                        ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                        : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
                    border: '1px solid ' + (data.transaction_type === 'credit' ? '#b7eb8f' : '#ffa39e'),
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Icon
                                type={data.transaction_type === 'credit' ? 'arrow-down' : 'arrow-up'}
                                style={{ color: '#fff', fontSize: 16 }}
                            />
                        </div>
                        <Tag color={data.transaction_type === 'credit' ? 'green' : 'red'} style={{ margin: 0 }}>
                            {data.transaction_type === 'credit' ? 'Crédito' : 'Débito'}
                        </Tag>
                    </div>

                    <div style={{ fontSize: 26, fontWeight: 700, color: data.transaction_type === 'credit' ? '#389e0d' : '#cf1322', lineHeight: 1.2, marginBottom: 6 }}>
                        R$ {toBRL(data.amount || 0)}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959', marginBottom: 12 }}>
                        <Icon type="calendar" />
                        <span>{moment(data.transaction_date).format('DD/MM/YYYY')}</span>
                        {data.document_number && (
                            <>
                                <span style={{ color: '#d9d9d9' }}>•</span>
                                <span>Doc: {data.document_number}</span>
                            </>
                        )}
                    </div>

                    <div style={{
                        background: 'rgba(255,255,255,0.7)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        fontSize: 12,
                        color: '#262626',
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                    }}>
                        <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 4 }}>
                            Descrição
                        </div>
                        {data.description || 'Sem descrição'}
                    </div>

                    {data.detected_customer && (
                        <div style={{ marginTop: 12, padding: '8px 12px', background: '#e6fffb', border: '1px solid #87e8de', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Icon type="idcard" style={{ color: '#13c2c2' }} />
                            <span style={{ color: '#595959' }}>Cliente detectado:</span>
                            <strong style={{ color: '#262626' }}>{data.detected_customer.name}</strong>
                        </div>
                    )}
                </div>

                {/* Note button below the card */}
                <Button
                    onClick={function() { setNoteDraft(note); setNoteModalOpen(true); }}
                    block
                    icon={note ? 'file-text' : 'form'}
                    style={{
                        marginTop: 12,
                        color: note ? '#1890ff' : '#595959',
                        borderColor: note ? '#91d5ff' : '#d9d9d9',
                        background: note ? '#e6f7ff' : '#fff',
                        fontWeight: 500,
                    }}
                >
                    {note ? 'Nota adicionada' : 'Adicionar nota'}
                </Button>
                {note && (
                    <div style={{
                        marginTop: 8,
                        padding: '8px 10px',
                        background: '#fafafa',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 6,
                        fontSize: 11,
                        color: '#595959',
                        fontStyle: 'italic',
                        maxHeight: 80,
                        overflow: 'auto',
                    }}>
                        "{note}"
                    </div>
                )}
            </div>

            {/* Right: main content */}
            <div style={{ flex: 1, minWidth: 0 }}>

            {/* Inline filter bar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ minWidth: 260 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#8c8c8c' }}>Período do vencimento</span>
                        {/* Cliente que acumula meses e paga tudo de uma vez faz a
                            cobrança vencer muito antes do recebimento cair. Sem esta
                            saída, ela não aparece na busca. */}
                        <Tooltip title="Busca em todo o histórico, sem limite de data. Útil quando o cliente acumulou meses e pagou tudo de uma vez.">
                            <Checkbox
                                checked={periodMode === 'all'}
                                onChange={function(e) {
                                    if (e.target.checked) {
                                        setPeriodMode('all');
                                        loadSuggestions('all', null, [null, null]);
                                    } else {
                                        var m = periodMonth || moment(data.transaction_date);
                                        setPeriodMonth(m);
                                        setPeriodMode('month');
                                        loadSuggestions('month', m, [null, null]);
                                    }
                                }}
                                style={{ fontSize: 11 }}
                            >
                                <span style={{ fontSize: 11 }}>Todo o histórico</span>
                            </Checkbox>
                        </Tooltip>
                    </div>
                    <div style={{ opacity: periodMode === 'all' ? 0.45 : 1, pointerEvents: periodMode === 'all' ? 'none' : 'auto' }}>
                    <MonthPicker
                        value={periodMonth ? periodMonth.format('YYYY-MM') : null}
                        onChange={function(val) {
                            var m = moment(val, 'YYYY-MM');
                            setPeriodMonth(m);
                            setPeriodMode('month');
                            loadSuggestions('month', m, [null, null]);
                        }}
                        onCustomRange={function(from, to) {
                            setPeriodMode('custom');
                            setPeriodRange([moment(from), moment(to)]);
                            loadSuggestions('custom', null, [moment(from), moment(to)]);
                        }}
                        customRangeLabel={
                            periodMode === 'custom' && periodRange[0] && periodRange[1]
                                ? periodRange[0].format('DD/MM/YY') + ' - ' + periodRange[1].format('DD/MM/YY')
                                : null
                        }
                        showTodayButton={false}
                        style={{ display: 'block', width: '100%' }}
                    />
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Cliente</div>
                    <Select
                        value={customerFilter || undefined}
                        placeholder="Todos os clientes"
                        style={{ width: '100%' }}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={function(input, option) {
                            return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        onChange={function(val) { setCustomerFilter(val || null); }}
                    >
                        {customers.map(function(c) {
                            return React.createElement(Select.Option, { key: c.id, value: c.id }, c.cod + ' - ' + c.name);
                        })}
                    </Select>
                </div>
                <div style={{ width: 1, height: 48, background: '#e8e8e8' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={function() { setMode('select'); }}
                        style={{ padding: '10px 18px', fontSize: 14, fontWeight: 500, border: '1.5px solid ' + (mode === 'select' ? '#1890ff' : '#d9d9d9'), borderRadius: 6, background: mode === 'select' ? '#e6f7ff' : '#fff', color: mode === 'select' ? '#1890ff' : '#595959', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}
                    ><Icon type="link" />Vincular</button>
                    <button onClick={function() { setMode('create'); }}
                        style={{ padding: '10px 18px', fontSize: 14, fontWeight: 500, border: '1.5px solid ' + (mode === 'create' ? '#1890ff' : '#d9d9d9'), borderRadius: 6, background: mode === 'create' ? '#e6f7ff' : '#fff', color: mode === 'create' ? '#1890ff' : '#595959', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}
                    ><Icon type="plus" />{data.transaction_type === 'debit' ? 'Nova despesa' : 'Nova receita'}</button>
                </div>
            </div>


            {/* Select Mode */}
            {mode === 'select' && (
                <div>
                    {selectedItems.length > 0 && (
                        <SelectionCard match={isExactMatch} isDebit={data.transaction_type === 'debit'}>
                            <SelectionText>
                                <Icon type="check-circle" theme="filled" style={{ color: '#52c41a' }} />
                                <span>
                                    <strong>{selectedItems.length} selecionado{selectedItems.length > 1 ? 's' : ''}</strong>
                                    <span style={{ margin: '0 8px', color: '#d9d9d9' }}>•</span>
                                    Total: R$ {toBRL(selectedTotal)}
                                    {isExactMatch && <Tag color="green" style={{ marginLeft: 8 }}>Valor exato</Tag>}
                                </span>
                            </SelectionText>
                            <Button size="small" type="link" icon="close" style={{ color: '#8c8c8c' }}
                                onClick={function() { setSelectedItems([]); }} />
                        </SelectionCard>
                    )}

                    {((isLoadingSuggestions || !suggestionsLoaded) && !searchText) ? (
                        <EmptyState>
                            <Spin size="large" />
                            <EmptyTitle style={{ marginTop: 16 }}>Buscando sugestões...</EmptyTitle>
                        </EmptyState>
                    ) : (suggestions.length > 0 || searchText) ? (
                        <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <span style={{ fontSize: 13, color: '#595959', fontWeight: 500 }}>
                                <Icon type="unordered-list" style={{ marginRight: 6 }} />
                                {sortedSuggestions.length} resultado(s) encontrado(s)
                                {isLoadingSuggestions && searchText && <Spin size="small" style={{ marginLeft: 8 }} />}
                            </span>
                        </div>
                        <SearchSortBar>
                            <Input
                                placeholder="Buscar em todo o histórico: nome, código do cliente, ME ou SOC..."
                                prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={function(e) { setSearchText(e.target.value); }}
                                allowClear
                                size="large"
                                style={{ flex: 1 }}
                            />
                            <SortButton
                                active={sortBy === 'name'}
                                onClick={function() { handleSortClick('name'); }}
                            >
                                Nome {getSortArrow('name')}
                            </SortButton>
                            <SortButton
                                active={sortBy === 'amount'}
                                onClick={function() { handleSortClick('amount'); }}
                            >
                                Valor {getSortArrow('amount')}
                            </SortButton>
                        </SearchSortBar>
                        {(function() {
                            if (sortedSuggestions.length === 0) {
                                var ctaLabel = data.transaction_type === 'credit' ? 'Nova receita' : 'Nova despesa';
                                return (
                                    <EmptyState>
                                        <EmptyIcon>
                                            <Icon type="search" />
                                        </EmptyIcon>
                                        <EmptyTitle>Nenhum resultado</EmptyTitle>
                                        <EmptyDescription style={{ marginBottom: 20 }}>
                                            Nenhum registro encontrado para conciliar com este lançamento.
                                            <br />
                                            Crie um novo lançamento para conciliá-lo.
                                        </EmptyDescription>
                                        <Button
                                            type="primary"
                                            icon="plus"
                                            size="large"
                                            onClick={function() { setMode('create'); }}
                                            style={{
                                                background: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d',
                                                borderColor: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d',
                                            }}
                                        >
                                            {ctaLabel}
                                        </Button>
                                    </EmptyState>
                                );
                            }
                            var stmtAmount = data.amount || 0;
                            // O título do código ME/SOC do lançamento é o boleto que
                            // foi pago, valha o que valer: fica num grupo próprio, no topo.
                            var codeItems = sortedSuggestions.filter(function(item) { return !!item.code_match; });
                            var restItems = sortedSuggestions.filter(function(item) { return !item.code_match; });
                            var exactItems = restItems.filter(function(item) {
                                return Math.abs(item.amount - stmtAmount) < 0.01;
                            });
                            var approximateItems = restItems.filter(function(item) {
                                var diff = Math.abs(item.amount - stmtAmount);
                                return diff >= 0.01 && diff / Math.max(stmtAmount, 0.01) <= 0.1;
                            });
                            var otherItems = restItems.filter(function(item) {
                                var diff = Math.abs(item.amount - stmtAmount);
                                return diff / Math.max(stmtAmount, 0.01) > 0.1;
                            });

                            var renderRow = function(item) {
                                var name = item.type === 'despesa'
                                    ? (item.description || 'Sem descrição')
                                    : (item.customer ? item.customer.name : 'Sem nome');
                                var cod = getItemCod(item);
                                var isSelected = selectedItems.some(function(sel) { return sel.id === item.id && sel.type === item.type; });
                                var isExact = Math.abs(item.amount - stmtAmount) < 0.01;
                                var typeInfo = getTypeInfo(item.type);

                                return (
                                    <tr
                                        key={item.type + '-' + item.id}
                                        onClick={function() { handleSelect(item); }}
                                        style={{
                                            cursor: 'pointer',
                                            background: isSelected ? '#e6f7ff' : '#fff',
                                            borderBottom: '1px solid #f0f0f0',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={function(e) { if (!isSelected) e.currentTarget.style.background = '#fafafa'; }}
                                        onMouseLeave={function(e) { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                                    >
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%',
                                                border: '2px solid ' + (isSelected ? '#1890ff' : '#d9d9d9'),
                                                background: isSelected ? '#1890ff' : '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {isSelected && <Icon type="check" style={{ color: '#fff', fontSize: 10 }} />}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 500, color: '#262626', maxWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                                {cod && (
                                                    <span style={{
                                                        fontSize: 11, fontWeight: 600, color: '#595959', background: '#f0f0f0',
                                                        border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 5px',
                                                        whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.02em',
                                                    }}>{cod}</span>
                                                )}
                                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {name}
                                                </span>
                                            </div>
                                            {item.is_learned_pattern && (
                                                <Tag color="purple" style={{ margin: '4px 4px 0 0', fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                                                    <Icon type="thunderbolt" style={{ marginRight: 2, fontSize: 9 }} />Padrão detectado{item.pattern_hits > 1 ? ' (' + item.pattern_hits + '×)' : ''}
                                                </Tag>
                                            )}
                                            {item.detected_customer && (
                                                <Tag color="cyan" style={{ margin: '4px 0 0', fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                                                    <Icon type="idcard" style={{ marginRight: 2, fontSize: 9 }} />CPF/CNPJ
                                                </Tag>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <Tag color={typeInfo.color} style={{ margin: 0 }}>{typeInfo.label}</Tag>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {(function() {
                                                var st = item.payment_status || 'pending';
                                                if (st === 'paid') return <Tag color="green" style={{ margin: 0, fontSize: 10 }}><Icon type="check-circle" style={{ fontSize: 9, marginRight: 2 }} />Pago</Tag>;
                                                if (st === 'partial') return <Tag color="gold" style={{ margin: 0, fontSize: 10 }}><Icon type="clock-circle" style={{ fontSize: 9, marginRight: 2 }} />Parcial</Tag>;
                                                return <Tag color="orange" style={{ margin: 0, fontSize: 10 }}><Icon type="exclamation-circle" style={{ fontSize: 9, marginRight: 2 }} />Pendente</Tag>;
                                            })()}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, color: '#8c8c8c' }}>
                                            {moment(item.due_at || item.created_at).format('DD/MM/YYYY')}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: isExact ? '#52c41a' : '#262626' }}>
                                            R$ {toBRL(item.amount)}
                                            {(function() {
                                                var parts = [];
                                                if (item.mensalidade_amount && item.mensalidade_amount !== item.amount) {
                                                    parts.push('Mens: R$ ' + toBRL(item.mensalidade_amount));
                                                }
                                                if (item.additionals_amount && item.additionals_amount > 0) {
                                                    parts.push('Adic: R$ ' + toBRL(item.additionals_amount));
                                                }
                                                if (item.amount13 && item.amount13 > 0) {
                                                    parts.push('13º: R$ ' + toBRL(item.amount13));
                                                }
                                                if (item.paid && item.paid > 0) {
                                                    parts.push('Pago: R$ ' + toBRL(item.paid));
                                                }
                                                if (item.paid13 && item.paid13 > 0) {
                                                    parts.push('13º pago: R$ ' + toBRL(item.paid13));
                                                }
                                                if (parts.length === 0) return null;
                                                return (
                                                    <Tooltip title={parts.join(' • ')}>
                                                        <div style={{ fontSize: 10, fontWeight: 400, color: '#8c8c8c', marginTop: 2 }}>
                                                            <Icon type="info-circle" style={{ marginRight: 3 }} />Saldo
                                                        </div>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                            {isExact && (
                                                <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                                                    <Icon type="check" style={{ fontSize: 9, marginRight: 2 }} />Exato
                                                </Tag>
                                            )}
                                        </td>
                                    </tr>
                                );
                            };

                            var tableHead = React.createElement('thead', null,
                                React.createElement('tr', { style: { background: '#fafafa', fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.03em' } },
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600 } }),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600 } }, 'Nome'),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600 } }, 'Tipo'),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600 } }, 'Status'),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600 } }, 'Data'),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'right', fontWeight: 600 } }, 'Valor'),
                                    React.createElement('th', { style: { padding: '8px 12px', textAlign: 'center', fontWeight: 600 } })
                                )
                            );

                            var colGroup = React.createElement('colgroup', null,
                                React.createElement('col', { style: { width: 36 } }),
                                React.createElement('col', null),
                                React.createElement('col', { style: { width: 110 } }),
                                React.createElement('col', { style: { width: 100 } }),
                                React.createElement('col', { style: { width: 100 } }),
                                React.createElement('col', { style: { width: 120 } }),
                                React.createElement('col', { style: { width: 80 } })
                            );

                            var tableStyle = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };
                            return (
                                <ListContainer>
                                    {codeItems.length > 0 && (
                                        <div>
                                            <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#0050b3', background: '#e6f7ff', borderBottom: '1px solid #91d5ff', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon type="barcode" style={{ fontSize: 12 }} />
                                                Código do lançamento ({codeItems.length})
                                            </div>
                                            <table style={tableStyle}>
                                                {colGroup}
                                                {tableHead}
                                                <tbody>{codeItems.map(renderRow)}</tbody>
                                            </table>
                                        </div>
                                    )}
                                    {exactItems.length > 0 && (
                                        <div>
                                            <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#389e0d', background: '#f6ffed', borderBottom: '1px solid #b7eb8f', borderTop: codeItems.length > 0 ? '2px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon type="check-circle" style={{ fontSize: 12 }} />
                                                Valores exatos ({exactItems.length})
                                            </div>
                                            <table style={tableStyle}>
                                                {colGroup}
                                                {!codeItems.length && tableHead}
                                                <tbody>{exactItems.map(renderRow)}</tbody>
                                            </table>
                                        </div>
                                    )}
                                    {approximateItems.length > 0 && (
                                        <div>
                                            <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#d48806', background: '#fffbe6', borderBottom: '1px solid #ffe58f', borderTop: exactItems.length > 0 ? '2px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon type="exclamation-circle" style={{ fontSize: 12 }} />
                                                Valores aproximados ({approximateItems.length})
                                            </div>
                                            <table style={tableStyle}>
                                                {colGroup}
                                                {(!exactItems.length && !codeItems.length) && tableHead}
                                                <tbody>{approximateItems.map(renderRow)}</tbody>
                                            </table>
                                        </div>
                                    )}
                                    {otherItems.length > 0 && (
                                        <div>
                                            <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#8c8c8c', background: '#fafafa', borderBottom: '1px solid #f0f0f0', borderTop: (exactItems.length > 0 || approximateItems.length > 0) ? '2px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon type="unordered-list" style={{ fontSize: 12 }} />
                                                Demais resultados ({otherItems.length})
                                            </div>
                                            <table style={tableStyle}>
                                                {colGroup}
                                                {(!codeItems.length && !exactItems.length && !approximateItems.length) && tableHead}
                                                <tbody>{otherItems.map(renderRow)}</tbody>
                                            </table>
                                        </div>
                                    )}
                                </ListContainer>
                            );
                        })()}
                </div>
            ) : (
                <EmptyState>
                    <EmptyIcon>
                        <Icon type="inbox" />
                    </EmptyIcon>
                    <EmptyTitle>Nenhum registro encontrado</EmptyTitle>
                    <EmptyDescription style={{ marginBottom: 20 }}>
                        Não há registros disponíveis para conciliar com este lançamento.
                        <br />
                        Crie um novo lançamento para conciliá-lo.
                    </EmptyDescription>
                    <Button
                        type="primary"
                        icon="plus"
                        size="large"
                        onClick={function() { setMode('create'); }}
                        style={{
                            background: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d',
                            borderColor: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d',
                        }}
                    >
                        {data.transaction_type === 'credit' ? 'Nova receita' : 'Nova despesa'}
                    </Button>
                </EmptyState>
            )}
                </div>
            )}

            {/* Create Mode */}
            {mode === 'create' && (() => {
                var isCredit = data.transaction_type === 'credit';
                var catList = isCredit ? categoriesReceber : categories;
                var diff = (parseFloat(data.amount) || 0) - customEntriesTotal;
                var isExact = Math.abs(diff) < 0.01;
                var diffColor = isExact ? '#52c41a' : '#faad14';
                var diffSign = isExact ? '' : (diff < 0 ? '- ' : '+ ');

                var updateEntry = function(index, patch) {
                    setCustomEntries(function(prev) {
                        var next = prev.slice();
                        next[index] = Object.assign({}, next[index], patch);
                        return next;
                    });
                };
                var addEntry = function() {
                    var remaining = (parseFloat(data.amount) || 0) - customEntriesTotal;
                    setCustomEntries(function(prev) {
                        return prev.concat([{ description: '', categoryId: '', amount: remaining > 0 ? remaining : 0, dueAt: moment(data.transaction_date) }]);
                    });
                };
                var removeEntry = function(index) {
                    setCustomEntries(function(prev) {
                        if (prev.length <= 1) return [{ description: '', categoryId: '', amount: 0, dueAt: moment(data.transaction_date) }];
                        var next = prev.slice();
                        next.splice(index, 1);
                        return next;
                    });
                };

                return (
                    <FormSection>
                        {/* Column headers — visible once at top */}
                        <div style={{
                            display: 'flex', gap: 8, alignItems: 'center',
                            padding: '0 8px 8px',
                            fontSize: 11, fontWeight: 600, color: '#8c8c8c',
                            textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                            <div style={{ width: 28, flexShrink: 0, textAlign: 'center' }}>#</div>
                            <div style={{ flex: 3, minWidth: 0 }}>Descrição *</div>
                            <div style={{ flex: 2, minWidth: 0 }}>Categoria</div>
                            <div style={{ width: 130, flexShrink: 0 }}>Data</div>
                            <div style={{ width: 140, flexShrink: 0, textAlign: 'right' }}>Valor *</div>
                            <div style={{ width: 28, flexShrink: 0 }} />
                        </div>

                        {/* Scrollable entry list — supports many rows without bloating the drawer */}
                        <div style={{
                            maxHeight: 'calc(100vh - 420px)',
                            overflowY: 'auto',
                            border: '1px solid #e8e8e8',
                            borderRadius: 8,
                            background: '#fff',
                        }}>
                            {customEntries.map(function(entry, idx) {
                                return (
                                    <div key={idx} style={{
                                        display: 'flex', gap: 8, alignItems: 'center',
                                        padding: '8px',
                                        borderBottom: idx < customEntries.length - 1 ? '1px solid #f0f0f0' : 'none',
                                        background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                    }}>
                                        <div style={{
                                            width: 28, height: 28, flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: '50%', background: '#f0f0f0',
                                            fontSize: 11, fontWeight: 600, color: '#595959',
                                        }}>
                                            {idx + 1}
                                        </div>

                                        <div style={{ flex: 3, minWidth: 0 }}>
                                            <FormInput
                                                type="text"
                                                placeholder="Descrição"
                                                value={entry.description}
                                                onChange={function(e) { updateEntry(idx, { description: e.target.value }); }}
                                                style={{ height: 36, padding: '6px 10px' }}
                                            />
                                        </div>

                                        <div style={{ flex: 2, minWidth: 0 }}>
                                            <Select
                                                showSearch
                                                allowClear
                                                value={entry.categoryId || undefined}
                                                onChange={function(val) { updateEntry(idx, { categoryId: val || '' }); }}
                                                placeholder="Categoria"
                                                style={{ width: '100%' }}
                                                optionFilterProp="children"
                                                filterOption={function(input, option) {
                                                    return (option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                                }}
                                            >
                                                {catList.map(function(cat) {
                                                    return <Select.Option key={cat.id} value={cat.id}>{cat.value}</Select.Option>;
                                                })}
                                            </Select>
                                        </div>

                                        <div style={{ width: 130, flexShrink: 0 }}>
                                            <DatePicker
                                                format="DD/MM/YYYY"
                                                value={entry.dueAt}
                                                onChange={function(val) { updateEntry(idx, { dueAt: val }); }}
                                                style={{ width: '100%' }}
                                                allowClear={false}
                                            />
                                        </div>

                                        <div style={{ width: 140, flexShrink: 0 }}>
                                            <InputCurrency
                                                value={entry.amount || 0}
                                                onChange={function(val) { updateEntry(idx, { amount: currency2Float(val) }); }}
                                                style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, height: 36, color: isCredit ? '#52c41a' : '#f5222d' }}
                                            />
                                        </div>

                                        <div style={{ width: 28, flexShrink: 0, textAlign: 'center' }}>
                                            {customEntries.length > 1 && (
                                                <Tooltip title="Remover lançamento">
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        icon="delete"
                                                        style={{ color: '#f5222d', padding: 0 }}
                                                        onClick={function() { removeEntry(idx); }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ padding: '6px 8px', fontSize: 12, color: '#8c8c8c' }}>
                            {customEntries.length} lançamento{customEntries.length !== 1 ? 's' : ''}
                        </div>


                        <Button
                            onClick={addEntry}
                            icon="plus"
                            block
                            style={{ borderStyle: 'dashed', marginBottom: 16 }}
                        >
                            Adicionar outro lançamento
                        </Button>

                        <InfoBox type={isCredit ? 'credit' : 'debit'}>
                            <InfoRow type={isCredit ? 'credit' : 'debit'}>
                                <InfoLabel>Valor do extrato</InfoLabel>
                                <InfoValue color={isCredit ? '#52c41a' : '#f5222d'}>R$ {toBRL(data.amount)}</InfoValue>
                            </InfoRow>
                            <InfoRow type={isCredit ? 'credit' : 'debit'}>
                                <InfoLabel>Total dos lançamentos</InfoLabel>
                                <InfoValue color="#1890ff">R$ {toBRL(customEntriesTotal)}</InfoValue>
                            </InfoRow>
                            <InfoRow type={isCredit ? 'credit' : 'debit'}>
                                <InfoLabel>Diferença</InfoLabel>
                                <InfoValue color={diffColor}>{diffSign}R$ {toBRL(Math.abs(diff))}</InfoValue>
                            </InfoRow>
                        </InfoBox>
                    </FormSection>
                );
            })()}

            </div>
            </div>

            {/* Note Modal */}
            <Modal
                title={<span><Icon type="form" style={{ marginRight: 6 }} />Nota da conciliação</span>}
                visible={noteModalOpen}
                onOk={function() { setNote(noteDraft); setNoteModalOpen(false); }}
                onCancel={function() { setNoteDraft(note); setNoteModalOpen(false); }}
                okText="Salvar"
                cancelText="Cancelar"
            >
                <p style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>
                    Esta nota será vinculada à conciliação como justificativa.
                </p>
                <Input.TextArea
                    rows={5}
                    value={noteDraft}
                    onChange={function(e) { setNoteDraft(e.target.value); }}
                    placeholder="Ex: Pagamento referente à mensalidade de fevereiro com desconto negociado..."
                    maxLength={500}
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: '#bfbfbf', marginTop: 4 }}>
                    {(noteDraft || '').length}/500
                </div>
            </Modal>

            {/* Footer */}
            <DrawerFooter>
                <ActionButton onClick={handleClose}>
                    Cancelar
                </ActionButton>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1, justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Valor do extrato</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: data.transaction_type === 'credit' ? '#52c41a' : '#f5222d' }}>
                            R$ {toBRL(data.amount || 0)}
                        </div>
                    </div>
                    <div style={{ width: 1, height: 30, background: '#e8e8e8' }} />
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Valor selecionado{selectedItems.length > 0 ? ' (' + selectedItems.length + ')' : ''}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: selectedItems.length ? '#1890ff' : '#bfbfbf' }}>
                            R$ {selectedItems.length ? toBRL(selectedTotal) : '0,00'}
                        </div>
                    </div>
                    <div style={{ width: 1, height: 30, background: '#e8e8e8' }} />
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Diferença</div>
                        {(function() {
                            if (!selectedItems.length) {
                                return <div style={{ fontSize: 16, fontWeight: 700, color: '#bfbfbf' }}>R$ 0,00</div>;
                            }
                            var diff = data.amount - selectedTotal;
                            var isExact = Math.abs(diff) < 0.01;
                            var color = isExact ? '#52c41a' : '#faad14';
                            var sign = isExact ? '' : (diff < 0 ? '- ' : '+ ');
                            return (
                                <div style={{ fontSize: 16, fontWeight: 700, color: color }}>
                                    {sign}R$ {toBRL(Math.abs(diff))}
                                </div>
                            );
                        })()}
                    </div>
                </div>
                <ActionButton
                    primary
                    onClick={handleSubmit}
                    disabled={isSubmitting || isDisabled}
                >
                    {isSubmitting ? (
                        <Spin size="small" />
                    ) : (
                        <>
                            <Icon type="check" />
                            Selecionar {selectedItems.length ? '(' + selectedItems.length + ')' : ''}
                        </>
                    )}
                </ActionButton>
            </DrawerFooter>
        </Drawer>
    );
};

export default DrawerReconcile;
